import React, { useState } from 'react';
import { Tractor, Users, TrendingUp, Clock, DollarSign, Wrench, ChevronDown, ChevronUp, CheckCircle, Target, Leaf, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Agri = () => {
  const [expandedStep, setExpandedStep] = useState(null);
  const navigate = useNavigate();

  const objectives = [
    "Enhance access to modern farm machinery for all farmers",
    "Improve productivity and reduce operational costs",
    "Support small and marginal farmers who cannot invest in expensive machinery",
    "Encourage sustainable and scientific agricultural practices",
    "Strengthen cooperative-driven farmer services at the village level"
  ];

  const benefits = [
    "Affordable access to high-cost machinery",
    "Reduced turnaround time for crop operations",
    "Improved efficiency and yield",
    "Lower dependency on manual labour",
    "Reliable services supported by local cooperatives",
    "Availability of trained operators and guidance"
  ];

  const processSteps = [
    { number: "1", title: "Farmer Approaches Nearest Cooperative Society", description: "Farmer visits PACCS or a designated cooperative service centre.", image: "/Images/TVM Region (2).jpg" },
    { number: "2", title: "Request for Machinery", description: "Society checks availability based on the type of machinery and required date.", image: "/Images/TVM Region (2).jpg" },
    { number: "3", title: "Request for Machinery", description: "Society checks availability based on the type of machinery and required date.", image: "/Images/TVM Region (2).jpg" },
    { number: "4", title: "Booking and Confirmation", description: "Farmer books machinery at standard rental rates approved by the department.", image: "/Images/TVM Region (2).jpg" },
    { number: "5", title: "Delivery or Pickup", description: "Machine is provided to the farmer, with operator support where required.", image: "/Images/TVM Region (2).jpg" },
    { number: "6", title: "Usage and Return", description: "Machinery is utilised for agricultural operations and returned in good condition.", image: "/Images/TVM Region (2).jpg" },
    { number: "7", title: "Payment and Records", description: "Society collects rental charges and updates usage records in its MIS.", image: "/Images/TVM Region (2).jpg" }
  ];

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-8 px-6 w-full">
        <h1 className="text-3xl md:text-4xl font-bold text-blue-900 text-center">
          Agri Mechanisation
        </h1>
      </div>

      {/* Overview Section */}
      <div className="w-full px-6 py-12">
        <div className="bg-blue-50 rounded-lg p-8 mb-12 w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Overview</h2>
          <div className="border-l-4 border-blue-600 pl-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              Agri Mechanisation services under the Cooperative Department support farmers by providing access to modern agricultural machinery at affordable rental rates. Through Primary Agricultural Cooperative Credit Societies (PACCS) and other cooperative institutions, Tamil Nadu enables small and marginal farmers to adopt efficient, time-saving, and cost-effective farming practices without the burden of owning high-value machinery. The initiative promotes improved productivity, reduces manual labour dependency, and ensures timely farm operations across the State.
            </p>
          </div>
        </div>

        {/* Key Objectives */}
        <div className="mb-12 w-full px-2">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Key Objectives</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {objectives.map((obj, index) => (
              <div
                key={index}
                className={`rounded-xl shadow-md w-full p-6 text-white
                  ${index % 3 === 0 ? 'bg-blue-500' : index % 3 === 1 ? 'bg-blue-400' : 'bg-gray-700'}`}
              >
                <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <p className="text-base">{obj}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-12 w-full px-2">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Benefits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-200 via-blue-100 to-blue-50 rounded-lg shadow p-6 hover:shadow-lg transition-shadow duration-200 w-full"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 text-white p-3 rounded-lg mt-1 flex-shrink-0">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <p className="text-blue-900 text-base flex-1">{benefit}</p>
                </div>
              </div>
            ))}
          </div>

          {/* E-Vadagai Services Button */}
          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate('/services/evadagai')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
            >
              E-Vadagai Services
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Process Flow - Two Steps per Row */}
        <div className="mb-12 w-full px-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-10 text-center">
            Process Flow
          </h2>

          <div className="flex flex-col gap-6">
            {processSteps.reduce((rows, step, index) => {
              if (index % 2 === 0) rows.push([step]);
              else rows[rows.length - 1].push(step);
              return rows;
            }, []).map((row, rowIndex) => (
              <div key={rowIndex} className="flex flex-col md:flex-row gap-6">
                {row.map((step) => (
                  <div key={step.number} className="relative bg-blue-50 p-6 rounded-lg shadow-md hover:shadow-lg transition duration-300 flex-1">
                    {/* Step Number Overlay */}
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                      {step.number}
                    </div>

                    {/* Step Content */}
                    <h3 className="text-lg font-semibold text-gray-800">{step.title}</h3>
                    <p className="text-gray-600 mt-2">{step.description}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Agri;